import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Routes,
  Route,
  useSearchParams,
  Link,
  useNavigate,
  Navigate
} from 'react-router-dom';
import axios from 'axios';

axios.defaults.withCredentials = true;

function LogoHeader() {
  return (
    <div className="d2t-logo">
      <img src="/images/sg-logo.png" alt="Starglaze logo" />
    </div>
  );
}

function FooterImage() {
  return (
    <div className="d2t-footer-card">
      <img src="/images/purpose.png" alt="Our purpose" />
    </div>
  );
}

// ------------------------------------------------------
// Date/time formatting helpers
// Converts UTC ISO strings to the user's local timezone
// ------------------------------------------------------
function formatDateTime(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  } catch { return isoString.slice(0, 16).replace('T', ' '); }
}

function formatDate(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch { return isoString.slice(0, 10); }
}

// ------------------------------------------------------
// Password complexity checker (mirrors backend rules)
// ------------------------------------------------------
function checkPasswordComplexity(password) {
  return {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };
}

function PasswordStrengthHints({ password }) {
  const checks = checkPasswordComplexity(password);
  const items = [
    { key: 'length',    label: 'At least 8 characters' },
    { key: 'uppercase', label: 'At least one uppercase letter' },
    { key: 'number',    label: 'At least one number' },
    { key: 'special',   label: 'At least one special character (!@#$% etc.)' }
  ];
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: '0.4rem 0 0', fontSize: '0.82rem' }}>
      {items.map(({ key, label }) => (
        <li key={key} style={{ color: checks[key] ? '#16a34a' : '#9ca3af' }}>
          {checks[key] ? '✓' : '○'} {label}
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------
// Helper hook: get current logged-in HR/admin user
// ------------------------------------------------------
function useMe() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get('/api/me');
        setUser(res.data);
      } catch {
        setUser(null);
      } finally {
        setChecked(true);
      }
    }
    load();
  }, []);

  return { user, setUser, checked };
}

// ------------------------------------------------------
// Helper hook: get current logged-in operator
// ------------------------------------------------------
function useOperator() {
  const [operator, setOperator] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get('/api/operator/me');
        setOperator(res.data);
      } catch {
        setOperator(null);
      } finally {
        setChecked(true);
      }
    }
    load();
  }, []);

  return { operator, setOperator, checked };
}

// ------------------------------------------------------
// Logout button (HR/Admin)
// ------------------------------------------------------
function LogoutButton({ onLoggedOut }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post('/api/logout');
    } catch {
      // ignore
    }
    if (onLoggedOut) onLoggedOut();
    navigate('/login');
  };

  return (
    <button
      type="button"
      className="d2t-button d2t-button--small"
      onClick={handleLogout}
    >
      Logout
    </button>
  );
}

// ------------------------------------------------------
// Operator Logout button
// ------------------------------------------------------
function OperatorLogoutButton({ onLoggedOut }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post('/api/operator/logout');
    } catch {
      // ignore
    }
    if (onLoggedOut) onLoggedOut();
    navigate('/operator-login');
  };

  return (
    <button
      type="button"
      className="d2t-button d2t-button--small"
      onClick={handleLogout}
    >
      Logout
    </button>
  );
}

// ------------------------------------------------------
// HR/Admin Login page
// ------------------------------------------------------
function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/authorise';

  const [email, setEmail] = useState('hradmin@starglaze-windows.com');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setSubmitting(true);
    try {
      await axios.post('/api/login', { email, password });
      navigate(redirect || '/authorise');
    } catch (err) {
      console.error('Login error', err);
      setMessage(err.response?.data?.error || 'Login failed. Check email and password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <h1 className="d2t-title">Drive to Thrive – Login</h1>
        <p className="d2t-subtitle">
          HR/admin users sign in here to authorise sign‑offs and manage guides.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="d2t-field">
            <label className="d2t-label">Email</label>
            <input
              type="email"
              className="d2t-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Password</label>
            <input
              type="password"
              className="d2t-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="d2t-button d2t-button--full"
            disabled={submitting}
          >
            {submitting ? 'Logging in…' : 'Login'}
          </button>
        </form>

        {message && (
          <p className="d2t-message d2t-message--error">{message}</p>
        )}

        <p className="d2t-help" style={{ marginTop: '0.75rem' }}>
          <Link to="/forgot-password">Forgot / change password</Link>
        </p>

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// Forgot password page (HR/Admin)
// ------------------------------------------------------
function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('info');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMsgType('info');
    setSubmitting(true);
    try {
      await axios.post('/api/forgot-password', { email });
      setMessage('If that email exists, a reset link has been sent. Please check your inbox.');
      setMsgType('success');
    } catch (err) {
      console.error('Forgot password error', err);
      setMessage('There was a problem sending the reset email. Please contact IT.');
      setMsgType('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <h1 className="d2t-title">Forgot / Change Password</h1>
        <p className="d2t-subtitle">
          Enter your email address and we'll send you a link to set a new password.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="d2t-field">
            <label className="d2t-label">Email</label>
            <input
              type="email"
              className="d2t-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="d2t-button d2t-button--full"
            disabled={submitting}
          >
            {submitting ? 'Sending…' : 'Send reset link'}
          </button>
        </form>

        {message && (
          <p className={`d2t-message ${msgType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>
            {message}
          </p>
        )}

        <p className="d2t-help" style={{ marginTop: '0.75rem' }}>
          <Link to="/login">← Back to login</Link>
        </p>

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// Reset password page (HR/Admin)
// ------------------------------------------------------
function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('info');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMsgType('info');

    if (!token) {
      setMessage('Missing or invalid reset token.');
      setMsgType('error');
      return;
    }
    if (password.length < 8) {
      setMessage('Password must be at least 8 characters.');
      setMsgType('error');
      return;
    }
    if (password !== confirm) {
      setMessage('Passwords do not match.');
      setMsgType('error');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/reset-password', { token, password });
      setMessage('Password updated. You can now log in with your new password.');
      setMsgType('success');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      console.error('Reset password error', err);
      setMessage(err.response?.data?.error || 'Unable to reset password. The link may have expired.');
      setMsgType('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <h1 className="d2t-title">Set New Password</h1>
        <p className="d2t-subtitle">Choose a strong password you haven't used before.</p>

        <form onSubmit={handleSubmit}>
          <div className="d2t-field">
            <label className="d2t-label">New password</label>
            <input
              type="password"
              className="d2t-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className="d2t-help">
              Use at least 8 characters, including upper/lower case, a number and a symbol.
            </p>
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Confirm password</label>
            <input
              type="password"
              className="d2t-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="d2t-button d2t-button--full"
            disabled={submitting}
          >
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>

        {message && (
          <p className={`d2t-message ${msgType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>
            {message}
          </p>
        )}

        <p className="d2t-help" style={{ marginTop: '0.75rem' }}>
          <Link to="/login">← Back to login</Link>
        </p>

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// OPERATOR: Login page (name + password)
// ------------------------------------------------------
function OperatorLoginPage() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setSubmitting(true);
    try {
      const res = await axios.post('/api/operator/login', { fullName, password });
      if (res.data.mustChangePassword) {
        navigate('/operator-change-password');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Operator login error', err);
      setMessage(err.response?.data?.error || 'Login failed. Please check your name and password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <h1 className="d2t-title">Drive to Thrive – Sign In</h1>
        <p className="d2t-subtitle">
          Enter your full name and password to access your sign-off form.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="d2t-field">
            <label className="d2t-label">Full name</label>
            <input
              type="text"
              className="d2t-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Michael Wilson"
              autoComplete="name"
              required
            />
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Password</label>
            <input
              type="password"
              className="d2t-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <button
            type="submit"
            className="d2t-button d2t-button--full"
            disabled={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {message && (
          <p className="d2t-message d2t-message--error">{message}</p>
        )}

        <div style={{
          marginTop: '1rem',
          padding: '0.75rem 1rem',
          background: '#f0f7ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#374151' }}>
            <strong>First time signing in, or forgotten your password?</strong>
          </p>
          <Link
            to="/operator-login-dob"
            className="d2t-button d2t-button--full"
            style={{ display: 'block', textDecoration: 'none', textAlign: 'center' }}
          >
            Verify with date of birth →
          </Link>
        </div>

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// OPERATOR: DOB verification (first login / forgot password)
// ------------------------------------------------------
function OperatorLoginDobPage() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setSubmitting(true);
    try {
      await axios.post('/api/operator/login-dob', { fullName, dateOfBirth });
      // Always redirects to change-password after DOB verification
      navigate('/operator-change-password');
    } catch (err) {
      console.error('Operator DOB login error', err);
      setMessage(err.response?.data?.error || 'Name or date of birth does not match our records.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <h1 className="d2t-title">Verify Your Identity</h1>
        <p className="d2t-subtitle">
          Enter your full name and date of birth. You will then be asked to set a password.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="d2t-field">
            <label className="d2t-label">Full name</label>
            <input
              type="text"
              className="d2t-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Michael Wilson"
              autoComplete="name"
              required
            />
            <p className="d2t-help">
              Enter your name exactly as it appears on your employment record.
            </p>
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Date of birth</label>
            <input
              type="date"
              className="d2t-input"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="d2t-button d2t-button--full"
            disabled={submitting}
          >
            {submitting ? 'Verifying…' : 'Verify identity'}
          </button>
        </form>

        {message && (
          <p className="d2t-message d2t-message--error">{message}</p>
        )}

        <p className="d2t-help" style={{ marginTop: '0.75rem' }}>
          <Link to="/operator-login">← Back to sign in</Link>
        </p>

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// OPERATOR: Change password (forced after DOB login)
// ------------------------------------------------------
function OperatorChangePasswordPage() {
  const navigate = useNavigate();
  const { operator, setOperator, checked } = useOperator();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('error');
  const [submitting, setSubmitting] = useState(false);

  // If not in a DOB-verified session at all, redirect to DOB page
  useEffect(() => {
    if (checked && !operator) {
      navigate('/operator-login-dob', { replace: true });
    }
  }, [checked, operator, navigate]);

  const checks = checkPasswordComplexity(password);
  const allChecksPassed = Object.values(checks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!allChecksPassed) {
      setMessage('Password does not meet all requirements.');
      setMsgType('error');
      return;
    }
    if (password !== confirm) {
      setMessage('Passwords do not match.');
      setMsgType('error');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/operator/change-password', { password });
      setMessage('Password set successfully. Taking you to the sign-off form…');
      setMsgType('success');
      // Update operator session state locally
      if (operator) setOperator({ ...operator, mustChangePassword: false });
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      console.error('Change password error', err);
      setMessage(err.response?.data?.error || 'Error setting password. Please try again.');
      setMsgType('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!checked) {
    return (
      <div className="d2t-page">
        <div className="d2t-card">
          <LogoHeader />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <h1 className="d2t-title">Set Your Password</h1>
        <p className="d2t-subtitle">
          {operator ? `Hi ${operator.fullName} — please` : 'Please'} choose a password you will use to sign in next time.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="d2t-field">
            <label className="d2t-label">New password</label>
            <input
              type="password"
              className="d2t-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <PasswordStrengthHints password={password} />
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Confirm password</label>
            <input
              type="password"
              className="d2t-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
            {confirm && password !== confirm && (
              <p className="d2t-help" style={{ color: '#dc2626' }}>Passwords do not match.</p>
            )}
          </div>

          <button
            type="submit"
            className="d2t-button d2t-button--full"
            disabled={submitting || !allChecksPassed || password !== confirm}
          >
            {submitting ? 'Setting password…' : 'Set password and continue'}
          </button>
        </form>

        {message && (
          <p className={`d2t-message ${msgType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>
            {message}
          </p>
        )}

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// OPERATOR: My Sign-off History
// ------------------------------------------------------
function MySignoffsPage() {
  const navigate = useNavigate();
  const { operator, setOperator, checked } = useOperator();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!checked) return;
    if (!operator) { navigate('/operator-login', { replace: true }); return; }
    if (operator.mustChangePassword) { navigate('/operator-change-password', { replace: true }); return; }
    let cancelled = false;
    axios.get('/api/operator/my-signoffs')
      .then(res => { if (!cancelled) setRows(res.data || []); })
      .catch(() => { if (!cancelled) setMessage('Error loading your sign-offs.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  const statusColor = s => s === 'Approved' ? '#16a34a' : s === 'Rejected' ? '#dc2626' : s === 'PendingApproval' ? '#d97706' : '#555';
  const statusLabel = s => s === 'ImmediateApplied' ? 'Applied (Level 0)' : s === 'PendingApproval' ? 'Pending approval' : s;

  if (!checked) return <div className="d2t-page"><div className="d2t-card"><LogoHeader /><p>Loading…</p></div></div>;
  if (!operator) return null;

  return (
    <div className="d2t-page"><div className="d2t-card">
      <LogoHeader />
      <div className="d2t-header-bar">
        <div>
          <h1 className="d2t-title">My Sign-off History</h1>
          <p className="d2t-subtitle">Signed in as <strong>{operator.fullName}</strong></p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to="/" className="d2t-button d2t-button--small">← Sign-off form</Link>
          <OperatorLogoutButton onLoggedOut={() => setOperator(null)} />
        </div>
      </div>
      {loading && <p>Loading your sign-offs…</p>}
      {message && <p className="d2t-message d2t-message--error">{message}</p>}
      {!loading && rows.length === 0 && <p>You have no sign-offs recorded yet.</p>}
      {!loading && rows.length > 0 && (
        <div className="d2t-table-wrapper">
          <table className="d2t-table">
            <thead>
              <tr>
                <th>Date</th><th>Area</th><th>Task</th><th>Guide title</th>
                <th>Version</th><th>Level</th><th>Status</th><th>Actioned by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td>{formatDate(r.signed_date)}</td>
                  <td>{r.area}</td>
                  <td>{r.task_code}</td>
                  <td>{r.title || '—'}</td>
                  <td>{r.doc_version || '—'}</td>
                  <td>{r.level}</td>
                  <td>
                    <span style={{ color: statusColor(r.status), fontWeight: 600 }}>
                      {statusLabel(r.status)}
                    </span>
                    {r.status === 'Rejected' && r.reject_reason && (
                      <><br /><small style={{ color: '#6b7280' }}>Reason: {r.reject_reason}</small></>
                    )}
                  </td>
                  <td>
                    {r.approved_by
                      ? <><span>{r.approved_by}</span><br /><small style={{ color: '#6b7280' }}>{formatDate(r.approved_at)}</small></>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FooterImage />
    </div></div>
  );
}

// ------------------------------------------------------
// Operative sign-off form
// Now reads identity from operator session — no dropdown
// ------------------------------------------------------
function SignoffForm() {
  const navigate = useNavigate();
  const { operator, setOperator, checked: operatorChecked } = useOperator();

  const [areas, setAreas] = useState([{ id: 1, name: 'Ali', display_name: 'Aluminium' }]);
  const [apiStatus, setApiStatus] = useState('Checking API…');
  const [area, setArea] = useState('');
  const [guides, setGuides] = useState([]);

  const [signatureName, setSignatureName] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [taskCode, setTaskCode] = useState('');
  const [docVersion, setDocVersion] = useState('');
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [level, setLevel] = useState(0);
  const [guideSearch, setGuideSearch] = useState('');

  const [checkboxes, setCheckboxes] = useState({
    readGuide: false,
    awarePPE: false,
    wearPPE: false,
    checkPPE: false,
    changePPE: false,
    knowGuideLocation: false,
    knowRiskAssessments: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [showAnotherModal, setShowAnotherModal] = useState(false);
  const [allDone, setAllDone] = useState(false);

  // Ref guard to prevent double-submission (e.g. from fast double-click or re-render)
  const isSubmittingRef = useRef(false);

  const allChecked = Object.values(checkboxes).every(Boolean);

  const filteredGuides = guides.filter(g => {
    if (!guideSearch) return true;
    const q = guideSearch.toLowerCase();
    return g.taskCode.toLowerCase().includes(q) || g.title.toLowerCase().includes(q);
  });

  // Guard: redirect to operator login if not authenticated
  useEffect(() => {
    if (!operatorChecked) return;
    if (!operator) {
      navigate('/operator-login', { replace: true });
      return;
    }
    // If they somehow got here without setting a password, push them back
    if (operator.mustChangePassword) {
      navigate('/operator-change-password', { replace: true });
    }
  }, [operatorChecked, operator, navigate]);

  useEffect(() => {
    axios.get('/api/health')
      .then((res) => setApiStatus(`API: ${res.data.status}`))
      .catch(() => setApiStatus('API: error – contact IT'));
  }, []);

  useEffect(() => {
    async function loadAreas() {
      try {
        const res = await axios.get('/api/areas');
        const list = res.data || [];
        if (list.length > 0) {
          setAreas(list);
          // Prefer the operator's default area from their Timeware dept,
          // fall back to the first area in the list if no match found.
          const preferred = operator?.defaultArea;
          if (preferred && list.some(a => a.name === preferred)) {
            setArea(preferred);
          } else {
            setArea(list[0].name);
          }
        }
      } catch (err) {
        console.error('Error loading areas', err);
      }
    }
    if (operator) loadAreas();
  }, [operator]);

  useEffect(() => {
    async function loadGuides() {
      try {
        const res = await axios.get(`/api/guides?area=${encodeURIComponent(area)}`);
        setGuides(res.data || []);
      } catch (err) {
        console.error('Error loading guides', err);
        setMessage('Error loading guides. Please contact your team leader.');
        setMessageType('error');
      }
    }
    if (area) loadGuides();  // only load when area is set
  }, [area]);

  const handleTaskChange = (e) => {
    const code = e.target.value;
    setTaskCode(code);
    const guide = guides.find((g) => g.taskCode === code);
    setSelectedGuide(guide || null);
    setDocVersion(guide ? guide.version || '' : '');
  };

  const handleCheckboxChange = (key) => {
    setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleOpenGuide = () => {
    if (!selectedGuide || !selectedGuide.filePath) return;
    window.open(selectedGuide.filePath, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Hard guard against double-submission
    if (isSubmittingRef.current) return;

    setMessage('');
    setMessageType('info');

    if (!taskCode) {
      setMessage('Please select a Drive to Thrive guide.');
      setMessageType('error');
      return;
    }
    if (!allChecked) {
      setMessage('Please confirm all statements before submitting.');
      setMessageType('error');
      return;
    }
    if (!signatureName.trim()) {
      setMessage('Please type your full name to sign electronically.');
      setMessageType('error');
      return;
    }

    setSubmitting(true);
    isSubmittingRef.current = true;
    try {
      await axios.post('/api/signoffs', {
        area,
        employeeId: Number(operator.employeeId),
        employeeName: operator.fullName,
        employeeEmail: operator.email || '',
        taskCode,
        docVersion,
        level: Number(level),
        date,
        checkboxesConfirmed: allChecked,
        signatureName: signatureName.trim()
      });

      const numericLevel = Number(level);
      const successMsg = numericLevel === 0
        ? 'Your Level 0 (awareness) has been recorded.'
        : `Your Level ${numericLevel} has been submitted and is pending HR authorisation.`;

      setMessage(successMsg);
      setMessageType('success');

      // Reset form fields ready for another sign-off
      setTaskCode('');
      setDocVersion('');
      setSelectedGuide(null);
      setLevel(0);
      setSignatureName('');
      setGuideSearch('');
      setCheckboxes({
        readGuide: false,
        awarePPE: false,
        wearPPE: false,
        checkPPE: false,
        changePPE: false,
        knowGuideLocation: false,
        knowRiskAssessments: false
      });

      // Show the "sign off another?" modal
      setShowAnotherModal(true);

    } catch (err) {
      console.error('Error submitting signoff', err);
      setMessage('There was an error submitting your sign‑off. Please try again or contact IT.');
      setMessageType('error');
    } finally {
      setSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const renderLevelHelp = () => {
    const lvl = Number(level);
    if (lvl === 0) {
      return (
        <p className="d2t-help">
          Level 0: base awareness level. This will be applied in TMS and HR will be notified.
        </p>
      );
    }
    return (
      <p className="d2t-help">
        Levels 1–3: these are higher competency levels and must be authorised before they appear in TMS.
      </p>
    );
  };

  if (!operatorChecked) {
    return (
      <div className="d2t-page">
        <div className="d2t-card">
          <LogoHeader />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!operator) return null;

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />

        <div className="d2t-header-bar">
          <div>
            <h1 className="d2t-title">Drive to Thrive – Sign Off</h1>
            <p className="d2t-subtitle">
              Signed in as <strong>{operator.fullName}</strong>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Link to="/my-signoffs" className="d2t-button d2t-button--small">My history</Link>
            <OperatorLogoutButton onLoggedOut={() => setOperator(null)} />
          </div>
        </div>

        <p className="d2t-help">{apiStatus}</p>

        <form onSubmit={handleSubmit}>
          <div className="d2t-field">
            <label className="d2t-label">Current area</label>
            <select
              className="d2t-select"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            >
              {areas.map((a) => (
                <option key={a.id} value={a.name}>
                  {a.display_name}
                </option>
              ))}
            </select>
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Date</label>
            <input
              type="date"
              className="d2t-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Task code / Drive to Thrive guide</label>
            <input
              type="text"
              className="d2t-input"
              placeholder="Search by code or title…"
              value={guideSearch}
              onChange={(e) => {
                setGuideSearch(e.target.value);
                setTaskCode('');
                setSelectedGuide(null);
                setDocVersion('');
              }}
              style={{ marginBottom: '0.4rem' }}
            />
            <select
              className="d2t-select"
              value={taskCode}
              onChange={handleTaskChange}
              required
            >
              <option value="">Select a guide</option>
              {filteredGuides.map((g) => (
                <option key={g.taskCode} value={g.taskCode}>
                  {g.taskCode} – {g.title}
                </option>
              ))}
            </select>
            {taskCode && (
              <p className="d2t-help">Document version: {docVersion || 'not specified'}</p>
            )}
            {selectedGuide && selectedGuide.filePath && (
              <button
                type="button"
                onClick={handleOpenGuide}
                className="d2t-button"
                style={{ marginTop: '0.5rem' }}
              >
                Open guide
              </button>
            )}
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Level</label>
            <select
              className="d2t-select"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              required
            >
              <option value={0}>0</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
            {renderLevelHelp()}
          </div>

          <hr style={{ margin: '1rem 0' }} />

          <div className="d2t-field">
            <p className="d2t-label">Please confirm all of the following:</p>
            <div className="d2t-checkbox-group">
              {[
                ['readGuide',           'I have read and understood this Drive to Thrive Guide.'],
                ['awarePPE',            'I am aware of the relevant PPE required to complete this task.'],
                ['wearPPE',             'I am aware of how to correctly wear the relevant PPE.'],
                ['checkPPE',            'I am aware of how to check my PPE is fit for purpose.'],
                ['changePPE',           'I am aware that if my PPE is not fit for purpose, I should change it immediately.'],
                ['knowGuideLocation',   'I know where to find this Drive to Thrive Guide if I need to refer to it in future.'],
                ['knowRiskAssessments', 'I know where to find the relevant Risk Assessments for the tasks I perform.']
              ].map(([key, label]) => (
                <label key={key} className="d2t-checkbox-item">
                  <input
                    type="checkbox"
                    checked={checkboxes[key]}
                    onChange={() => handleCheckboxChange(key)}
                  />{' '}
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="d2t-field">
            <label className="d2t-label">Type your full name to sign electronically</label>
            <input
              className="d2t-input"
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="e.g. Michael Wilson"
              required
            />
            <p className="d2t-help">
              This acts as your electronic signature to confirm you agree with the statements above.
            </p>
          </div>

          <button
            type="submit"
            className="d2t-button d2t-button--full"
            disabled={submitting || !allChecked || !taskCode}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </form>

        {message && !showAnotherModal && (
          <p className={`d2t-message ${messageType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>
            {message}
          </p>
        )}

        {/* "Sign off another guide?" modal */}
        {showAnotherModal && !allDone && (
          <div style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#fff', borderRadius: '10px',
              padding: '1.75rem', maxWidth: '420px', width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)', textAlign: 'center'
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.5rem', color: '#16a34a' }}>
                Sign-off submitted!
              </h2>
              <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '1.25rem' }}>
                {message}
              </p>
              <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1a4a8a', marginBottom: '1rem' }}>
                Would you like to sign off another guide?
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <button
                  type="button"
                  className="d2t-button"
                  onClick={() => {
                    setShowAnotherModal(false);
                    setMessage('');
                  }}
                >
                  Yes — sign off another
                </button>
                <button
                  type="button"
                  className="d2t-button d2t-button--secondary"
                  onClick={() => {
                    setShowAnotherModal(false);
                    setAllDone(true);
                  }}
                >
                  No — I'm done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* All done screen */}
        {allDone && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1.5rem',
            background: '#f0fdf4',
            border: '2px solid #16a34a',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.5rem' }}>
              All done — thank you!
            </h2>
            <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '1rem' }}>
              Your sign-off has been recorded. You can safely close this page.
            </p>
            <button
              type="button"
              className="d2t-button"
              onClick={() => {
                setAllDone(false);
                setMessage('');
              }}
            >
              Sign off another guide
            </button>
          </div>
        )}

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// Authorisation page (HR/Admin — protected)
// ------------------------------------------------------
function AuthorisePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { user, setUser, checked: authChecked } = useMe();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [signoff, setSignoff] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const [actionType, setActionType] = useState('info');
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      if (!authChecked) return;

      if (!user) {
        const redirectUrl = token
          ? `/authorise?token=${encodeURIComponent(token)}`
          : '/authorise';
        navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`, { replace: true });
        return;
      }

      if (!token) {
        setLoadError('Missing token in URL.');
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`/api/signoffs/by-token?token=${encodeURIComponent(token)}`);
        setSignoff(res.data);
      } catch (err) {
        console.error('Error loading signoff', err);
        setLoadError(err.response?.data?.error || 'Error loading sign-off. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [authChecked, user, token, navigate]);

  const handleApprove = async () => {
    if (!signoff) return;
    setSubmitting(true);
    setActionMessage('');
    try {
      await axios.post(`/api/signoffs/${signoff.id}/approve`);
      setActionMessage('Sign-off approved successfully.');
      setActionType('success');
      setSignoff({ ...signoff, status: 'Approved' });
    } catch (err) {
      console.error('Error approving signoff', err);
      setActionMessage('Error approving sign-off. Please try again or contact IT.');
      setActionType('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!signoff) return;
    if (!rejectReason.trim()) {
      setActionMessage('Please provide a reason for rejection.');
      setActionType('error');
      return;
    }
    setSubmitting(true);
    setActionMessage('');
    try {
      await axios.post(`/api/signoffs/${signoff.id}/reject`, { reason: rejectReason });
      setActionMessage('Sign-off rejected.');
      setActionType('success');
      setSignoff({ ...signoff, status: 'Rejected', reject_reason: rejectReason });
    } catch (err) {
      console.error('Error rejecting signoff', err);
      setActionMessage('Error rejecting sign-off. Please try again or contact IT.');
      setActionType('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authChecked) {
    return (
      <div className="d2t-page">
        <div className="d2t-card">
          <LogoHeader />
          <h1 className="d2t-title">Drive to Thrive – Authorisation</h1>
          <p>Checking login…</p>
        </div>
      </div>
    );
  }

  let statusColor = '#555';
  if (signoff?.status === 'Approved') statusColor = 'green';
  if (signoff?.status === 'Rejected') statusColor = '#c62828';

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <div className="d2t-header-bar">
          <div>
            <h1 className="d2t-title">Drive to Thrive – Authorisation</h1>
            {user && (
              <p className="d2t-subtitle">Logged in as {user.email} ({user.role})</p>
            )}
          </div>
          {user && <LogoutButton onLoggedOut={() => setUser(null)} />}
        </div>

        {loading && <p>Loading sign-off…</p>}
        {loadError && <p className="d2t-message d2t-message--error">{loadError}</p>}

        {!loading && !loadError && signoff && (
          <>
            <p>
              <strong>Status: </strong>
              <span style={{ color: statusColor }}>{signoff.status}</span>
            </p>

            <div style={{ marginBottom: '0.75rem', fontSize: '0.92rem' }}>
              <p><strong>Employee:</strong> {signoff.employee_name} (ID: {signoff.employee_id})</p>
              <p><strong>Area:</strong> {signoff.area}</p>
              <p><strong>Task code:</strong> {signoff.task_code}</p>
              <p><strong>Version:</strong> {signoff.doc_version || 'not specified'}</p>
              <p><strong>Level:</strong> {signoff.level}</p>
              <p><strong>Signed date:</strong> {signoff.signed_date}</p>
              <p><strong>Created at:</strong> {formatDateTime(signoff.created_at)}</p>
              {signoff.approved_by && (
                <p><strong>Last action by:</strong> {signoff.approved_by} at {formatDateTime(signoff.approved_at)}</p>
              )}
              {signoff.reject_reason && (
                <p><strong>Reject reason:</strong> {signoff.reject_reason}</p>
              )}
            </div>

            {signoff.status === 'PendingApproval' && (
              <>
                <div style={{ marginBottom: '0.75rem' }}>
                  <button
                    type="button"
                    className="d2t-button"
                    onClick={handleApprove}
                    disabled={submitting}
                  >
                    {submitting ? 'Working…' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    className="d2t-button d2t-button--danger"
                    onClick={handleReject}
                    disabled={submitting}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    {submitting ? 'Working…' : 'Reject'}
                  </button>
                </div>

                <div className="d2t-field">
                  <label className="d2t-label">Reject reason (required to reject)</label>
                  <textarea
                    className="d2t-textarea"
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
              </>
            )}

            {actionMessage && (
              <p className={`d2t-message ${actionType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>
                {actionMessage}
              </p>
            )}
          </>
        )}

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// Admin page
// ------------------------------------------------------
function AdminPage() {
  const navigate = useNavigate();
  const { user, setUser, checked } = useMe();

  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState('Ali');
  const [reportArea, setReportArea] = useState('Ali');

  const [timewareAreas, setTimewareAreas] = useState([]);
  const [selectedTimewareArea, setSelectedTimewareArea] = useState('');

  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('info');
  const [showInactive, setShowInactive] = useState(false);

  const [newTaskCode, setNewTaskCode] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newVersion, setNewVersion] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [savingGuide, setSavingGuide] = useState(false);

  const [deleteIds, setDeleteIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);

  const [newAreaName, setNewAreaName] = useState('');
  const [newAreaDisplayName, setNewAreaDisplayName] = useState('');
  const [savingArea, setSavingArea] = useState(false);

  const [overrides, setOverrides] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [ovEmployeeId, setOvEmployeeId] = useState('');
  const [ovFromArea, setOvFromArea] = useState('Ali');
  const [ovToArea, setOvToArea] = useState('Ali');
  const [ovStartDate, setOvStartDate] = useState('');
  const [ovEndDate, setOvEndDate] = useState('');
  const [ovSaving, setOvSaving] = useState(false);
  const [ovDeletingId, setOvDeletingId] = useState(null);

  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportAllAreas, setReportAllAreas] = useState(false);
  const [pendingCount, setPendingCount] = useState(null);

  const loadAreas = async () => {
    try {
      const res = await axios.get('/api/admin/areas');
      const list = res.data || [];
      setAreas(list);
      if (list.length > 0 && !list.some(a => a.name === area)) {
        setArea(list[0].name);
      }
    } catch (err) {
      console.error('Error loading admin areas', err);
    }
  };

  const loadGuides = async (currentArea) => {
    setLoading(true);
    setMessage('');
    try {
      const res = await axios.get(`/api/admin/guides?area=${encodeURIComponent(currentArea)}`);
      const allGuides = res.data || [];
      setGuides(showInactive ? allGuides : allGuides.filter(g => g.active));
    } catch (err) {
      console.error('Error loading admin guides', err);
      setMessage('Error loading guides. Please contact IT.');
      setMsgType('error');
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (currentArea) => {
    setReportLoading(true);
    try {
      const res = reportAllAreas
        ? await axios.get('/api/admin/signoffs-report-all')
        : await axios.get(`/api/admin/signoffs-report?area=${encodeURIComponent(currentArea)}`);
      setReportRows(res.data || []);
      setShowReport(true);
    } catch (err) {
      console.error('signoffs report error', err);
      setMessage(err.response?.data?.error || 'Error loading sign-offs report.');
      setMsgType('error');
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportCsv = () => {
    const url = reportAllAreas
      ? '/api/admin/signoffs-report/export?all=true'
      : `/api/admin/signoffs-report/export?area=${encodeURIComponent(reportArea)}`;
    window.open(url, '_blank');
  };

  const loadOverrides = async () => {
    setOverridesLoading(true);
    try {
      const res = await axios.get('/api/admin/area-overrides');
      setOverrides(res.data || []);
    } catch (err) {
      console.error('Error loading area overrides', err);
    } finally {
      setOverridesLoading(false);
    }
  };

  useEffect(() => {
    if (!checked) return;
    if (!user) {
      navigate('/login?redirect=/admin', { replace: true });
      return;
    }
    if (user.role !== 'admin') {
      setMessage('You do not have admin access.');
      setMsgType('error');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        await loadAreas();
        const res = await axios.get('/api/admin/timeware-areas');
        const twa = res.data || [];
        setTimewareAreas(twa);
        if (!selectedTimewareArea && twa.length > 0) setSelectedTimewareArea(twa[0].key);
        await loadGuides(area);
        await loadOverrides();
      } catch (err) {
        console.error('Admin init error', err);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, user, area, navigate, showInactive]);

  const handleDeleteInactiveGuides = async () => {
    if (!showInactive) {
      setMessage('You must tick "Show inactive guides" before you can permanently delete inactive guides.');
      setMsgType('error');
      return;
    }
    if (!Array.isArray(deleteIds) || deleteIds.length === 0) {
      setMessage('No guides selected for permanent deletion.');
      setMsgType('error');
      return;
    }
    if (!window.confirm('Permanently delete the selected inactive guides? This cannot be undone.')) return;
    setMessage('');
    try {
      await axios.post('/api/admin/guides/delete-inactive', { ids: deleteIds });
      await loadGuides(area);
      setDeleteIds([]);
      setMessage('Selected inactive guides have been permanently deleted.');
      setMsgType('success');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error permanently deleting selected inactive guides.');
      setMsgType('error');
    }
  };

  const handleAddGuide = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!newTaskCode || !newTitle) { setMessage('Task code and title are required.'); setMsgType('error'); return; }
    if (!newFile) { setMessage('Please select a PDF file to upload.'); setMsgType('error'); return; }
    setSavingGuide(true);
    try {
      const formData = new FormData();
      formData.append('area', area);
      formData.append('taskCode', newTaskCode);
      formData.append('title', newTitle);
      formData.append('version', newVersion);
      formData.append('notes', newNotes);
      formData.append('file', newFile);
      await axios.post('/api/admin/guides/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadGuides(area);
      setNewTaskCode(''); setNewTitle(''); setNewVersion(''); setNewNotes(''); setNewFile(null);
      setMessage('Guide uploaded and added.'); setMsgType('success');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error adding guide.'); setMsgType('error');
    } finally {
      setSavingGuide(false);
    }
  };

  const toggleDeleteId = (id) => {
    setDeleteIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleDeleteSelected = async () => {
    if (deleteIds.length === 0) { setMessage('No guides selected for deletion.'); setMsgType('error'); return; }
    if (!window.confirm('Mark selected guides as deleted (inactive)?')) return;
    setDeleting(true);
    setMessage('');
    try {
      await axios.post('/api/admin/guides/delete', { ids: deleteIds });
      await loadGuides(area);
      setDeleteIds([]);
      setMessage('Selected guides have been marked inactive.'); setMsgType('success');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error deleting guides.'); setMsgType('error');
    } finally {
      setDeleting(false);
    }
  };

  const handleReactivate = async (id) => {
    setReactivatingId(id);
    setMessage('');
    try {
      await axios.post('/api/admin/guides/reactivate', { id });
      await loadGuides(area);
      setMessage('Guide reactivated.'); setMsgType('success');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error reactivating guide.'); setMsgType('error');
    } finally {
      setReactivatingId(null);
    }
  };

  const handleAddArea = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!newAreaName || !newAreaDisplayName) { setMessage('Area name and display name are required.'); setMsgType('error'); return; }
    if (!selectedTimewareArea) { setMessage('Please choose a Timeware area (Dept).'); setMsgType('error'); return; }
    setSavingArea(true);
    try {
      await axios.post('/api/admin/areas', { name: newAreaName, displayName: newAreaDisplayName, deptName: selectedTimewareArea });
      setNewAreaName(''); setNewAreaDisplayName('');
      await loadAreas();
      setMessage('Area added.'); setMsgType('success');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error adding area.'); setMsgType('error');
    } finally {
      setSavingArea(false);
    }
  };

  const handleAddOverride = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!ovEmployeeId || !ovFromArea || !ovToArea || !ovStartDate || !ovEndDate) {
      setMessage('Employee ID, from area, to area, start date and end date are required.'); setMsgType('error'); return;
    }
    if (ovFromArea === ovToArea) { setMessage('From area and to area cannot be the same.'); setMsgType('error'); return; }
    setOvSaving(true);
    try {
      await axios.post('/api/admin/area-overrides', { employeeId: Number(ovEmployeeId), fromArea: ovFromArea, toArea: ovToArea, startDate: ovStartDate, endDate: ovEndDate });
      setOvEmployeeId(''); setOvStartDate(''); setOvEndDate('');
      await loadOverrides();
      setMessage('Area override created.'); setMsgType('success');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error creating area override.'); setMsgType('error');
    } finally {
      setOvSaving(false);
    }
  };

  const handleDeleteOverride = async (id) => {
    if (!window.confirm('Delete this override?')) return;
    setOvDeletingId(id);
    try {
      await axios.delete(`/api/admin/area-overrides/${id}`);
      await loadOverrides();
      setMessage('Area override deleted.'); setMsgType('success');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error deleting area override.'); setMsgType('error');
    } finally {
      setOvDeletingId(null);
    }
  };

  if (!checked) {
    return (
      <div className="d2t-page"><div className="d2t-card"><LogoHeader /><h1 className="d2t-title">Drive to Thrive – Admin</h1><p>Checking login...</p></div></div>
    );
  }
  if (!user) return null;

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <div className="d2t-header-bar">
          <div>
            <h1 className="d2t-title">Drive to Thrive – Admin</h1>
            <p className="d2t-subtitle">Manage areas and guides.</p>
          </div>
          <div className="d2t-help">
            {user.email} ({user.role}){' '}
            <LogoutButton onLoggedOut={() => setUser(null)} />
          </div>
        </div>

        {/* Quick navigation dashboard */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem', marginTop: '0.5rem' }}>
          {[
            { to: '/admin/pending',   label: 'Pending Approvals',  desc: 'Review & action sign-offs awaiting authorisation',          color: '#d97706', badge: pendingCount > 0 ? pendingCount : null },
            { to: '/admin/operators', label: 'Operator Accounts',  desc: 'View accounts, last login, reset passwords',                color: '#1a4a8a' },
            { to: '/admin/audit',     label: 'Audit Log',          desc: 'Full trail of logins, sign-offs and admin actions',         color: '#374151' },
            { to: '/admin/users',     label: 'Manage Users',       desc: 'Create, edit and delete HR / Admin accounts',               color: '#1a4a8a' },
          ].map(({ to, label, desc, color, badge }) => (
            <Link
              key={to}
              to={to}
              style={{
                display: 'block',
                textDecoration: 'none',
                background: '#fff',
                border: `2px solid ${color}`,
                borderRadius: '8px',
                padding: '0.85rem 1rem',
                position: 'relative',
                transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              {badge && (
                <span style={{
                  position: 'absolute', top: '0.5rem', right: '0.6rem',
                  background: '#dc2626', color: '#fff', borderRadius: '999px',
                  padding: '0 0.45rem', fontSize: '0.72rem', fontWeight: 700
                }}>{badge}</span>
              )}
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color, marginBottom: '0.3rem' }}>{label}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.3 }}>{desc}</div>
            </Link>
          ))}
        </div>

        <h2 className="d2t-title" style={{ fontSize: '1.1rem', marginTop: '0.5rem' }}>Areas</h2>

        <div className="d2t-field">
          <label className="d2t-label">Current area</label>
          <select className="d2t-select" value={area} onChange={(e) => { setArea(e.target.value); setReportArea(e.target.value); }}>
            {areas.map((a) => <option key={a.id} value={a.name}>{a.display_name}</option>)}
          </select>
        </div>

        <form onSubmit={handleAddArea}>
          <div className="d2t-field">
            <label className="d2t-label">Timeware area (Dept)</label>
            <select className="d2t-select" value={selectedTimewareArea}
              onChange={(e) => { const key = e.target.value; setSelectedTimewareArea(key); setNewAreaName(key); setNewAreaDisplayName(key); }} required>
              {timewareAreas.map(a => <option key={a.key} value={a.key}>{a.displayName}</option>)}
            </select>
            <p className="d2t-help">This list comes from Timeware. You can only add areas that exist there.</p>
          </div>
          <div className="d2t-field">
            <label className="d2t-label">Display name (what operatives see)</label>
            <input className="d2t-input" value={newAreaDisplayName} onChange={(e) => setNewAreaDisplayName(e.target.value)} required />
          </div>
          <button type="submit" className="d2t-button d2t-button--full" disabled={savingArea}>
            {savingArea ? 'Saving…' : 'Add area'}
          </button>
        </form>

        <h2 className="d2t-title" style={{ fontSize: '1.1rem', marginTop: '1.25rem' }}>Area overrides</h2>
        <p className="d2t-help">Temporarily treat an employee as being in a different area for a given date range.</p>

        <form onSubmit={handleAddOverride}>
          <div className="d2t-field">
            <label className="d2t-label">Employee ID</label>
            <input className="d2t-input" type="number" value={ovEmployeeId} onChange={(e) => setOvEmployeeId(e.target.value)} placeholder="Enter Timeware employee ID" required />
          </div>
          <div className="d2t-field">
            <label className="d2t-label">From area</label>
            <select className="d2t-select" value={ovFromArea} onChange={(e) => setOvFromArea(e.target.value)}>
              {areas.map((a) => <option key={a.id} value={a.name}>{a.display_name}</option>)}
            </select>
          </div>
          <div className="d2t-field">
            <label className="d2t-label">To area</label>
            <select className="d2t-select" value={ovToArea} onChange={(e) => setOvToArea(e.target.value)}>
              {areas.map((a) => <option key={a.id} value={a.name}>{a.display_name}</option>)}
            </select>
          </div>
          <div className="d2t-field">
            <label className="d2t-label">Start date</label>
            <input type="date" className="d2t-input" value={ovStartDate} onChange={(e) => setOvStartDate(e.target.value)} required />
          </div>
          <div className="d2t-field">
            <label className="d2t-label">End date</label>
            <input type="date" className="d2t-input" value={ovEndDate} onChange={(e) => setOvEndDate(e.target.value)} required />
          </div>
          <button type="submit" className="d2t-button d2t-button--full" disabled={ovSaving}>
            {ovSaving ? 'Saving override…' : 'Add area override'}
          </button>
        </form>

        <h3 className="d2t-title" style={{ fontSize: '1rem', marginTop: '1rem' }}>Existing overrides</h3>
        {overridesLoading ? <p>Loading overrides…</p> : overrides.length === 0 ? <p>No area overrides defined.</p> : (
          <div className="d2t-table-wrapper">
            <table className="d2t-table">
              <thead><tr><th>Employee</th><th>From</th><th>To</th><th>Start</th><th>End</th><th>Actions</th></tr></thead>
              <tbody>
                {overrides.map((ov) => (
                  <tr key={ov.id}>
                    <td>{ov.employee_name ? `${ov.employee_name} (ID: ${ov.employee_id})` : `ID: ${ov.employee_id}`}</td>
                    <td>{ov.from_area}</td><td>{ov.to_area}</td><td>{formatDate(ov.start_date)}</td><td>{formatDate(ov.end_date)}</td>
                    <td>
                      <button type="button" className="d2t-button d2t-button--small d2t-button--danger"
                        onClick={() => handleDeleteOverride(ov.id)} disabled={ovDeletingId === ov.id}>
                        {ovDeletingId === ov.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loading && <p style={{ marginTop: '1rem' }}>Loading guides…</p>}

        {!loading && (
          <>
            <div className="d2t-field" style={{ marginTop: '0.75rem' }}>
              <label className="d2t-label">
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />{' '}
                Show inactive guides
              </label>
              <p className="d2t-help">When unticked, only active guides are listed.</p>
            </div>

            <h2 className="d2t-title" style={{ fontSize: '1.1rem', marginTop: '1.25rem' }}>Existing guides ({area})</h2>

            {guides.length === 0 ? <p>No guides found for this area.</p> : (
              <>
                <div className="d2t-table-wrapper">
                  <table className="d2t-table">
                    <thead><tr><th>Delete</th><th>Task</th><th>Title</th><th>Version</th><th>PDF</th><th>Notes</th><th>Active</th>{showInactive && <th>Restore</th>}</tr></thead>
                    <tbody>
                      {guides.map((g) => (
                        <tr key={g.id}>
                          <td><input type="checkbox" checked={deleteIds.includes(g.id)} onChange={() => toggleDeleteId(g.id)} /></td>
                          <td>{g.task_code}</td><td>{g.title}</td><td>{g.version}</td>
                          <td>{g.file_path ? <a href={g.file_path} target="_blank" rel="noopener noreferrer">View</a> : <span style={{ color: '#999' }}>None</span>}</td>
                          <td>{g.notes}</td><td>{g.active ? 'Yes' : 'No'}</td>
                          {showInactive && (
                            <td>
                              {!g.active && (
                                <button
                                  type="button"
                                  className="d2t-button d2t-button--small"
                                  onClick={() => handleReactivate(g.id)}
                                  disabled={reactivatingId === g.id}
                                >
                                  {reactivatingId === g.id ? 'Restoring…' : 'Restore'}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="d2t-button d2t-button--danger" style={{ marginTop: '0.75rem' }} disabled={deleting || deleteIds.length === 0} onClick={handleDeleteSelected}>
                  {deleting ? 'Deleting…' : 'Delete selected guides'}
                </button>
                {showInactive && (
                  <button type="button" className="d2t-button d2t-button--danger" style={{ marginTop: '0.75rem', marginLeft: '0.75rem' }} disabled={deleteIds.length === 0} onClick={handleDeleteInactiveGuides}>
                    Permanently delete selected inactive guides
                  </button>
                )}
              </>
            )}

            <h2 className="d2t-title" style={{ fontSize: '1.1rem', marginTop: '1.25rem' }}>Upload new guide (PDF)</h2>
            <form onSubmit={handleAddGuide}>
              <div className="d2t-field"><label className="d2t-label">Task code</label><input className="d2t-input" value={newTaskCode} onChange={(e) => setNewTaskCode(e.target.value)} required /></div>
              <div className="d2t-field"><label className="d2t-label">Title</label><input className="d2t-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required /></div>
              <div className="d2t-field"><label className="d2t-label">Version</label><input className="d2t-input" value={newVersion} onChange={(e) => setNewVersion(e.target.value)} /></div>
              <div className="d2t-field"><label className="d2t-label">Notes</label><textarea className="d2t-textarea" rows={2} value={newNotes} onChange={(e) => setNewNotes(e.target.value)} /></div>
              <div className="d2t-field">
                <label className="d2t-label">PDF file</label>
                <input type="file" accept="application/pdf" onChange={(e) => setNewFile(e.target.files?.[0] || null)} className="d2t-input" />
                <p className="d2t-help">Only PDF files are allowed.</p>
              </div>
              <button type="submit" className="d2t-button d2t-button--full" disabled={savingGuide}>{savingGuide ? 'Uploading...' : 'Upload guide'}</button>
            </form>

            <h2 className="d2t-title" style={{ fontSize: '1.1rem', marginTop: '1.5rem' }}>Sign-offs report</h2>

            <div className="d2t-field">
              <label className="d2t-label">
                <input
                  type="checkbox"
                  checked={reportAllAreas}
                  onChange={(e) => { setReportAllAreas(e.target.checked); setShowReport(false); }}
                />{' '}
                Show all areas
              </label>
            </div>

            {!reportAllAreas && (
              <div className="d2t-field">
                <label className="d2t-label">Report area</label>
                <select className="d2t-select" value={reportArea} onChange={(e) => { setReportArea(e.target.value); setShowReport(false); }}>
                  {areas.map((a) => <option key={a.id} value={a.name}>{a.display_name}</option>)}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button type="button" className="d2t-button" disabled={reportLoading}
                onClick={async () => { if (showReport) { setShowReport(false); } else { await loadReport(reportArea); } }}>
                {reportLoading ? 'Loading report…' : showReport ? 'Close report' : 'Load report'}
              </button>
              <button type="button" className="d2t-button" onClick={handleExportCsv}>
                Export CSV ↓
              </button>
            </div>

            {showReport && reportRows.length === 0 && !reportLoading && <p>No sign-offs found.</p>}
            {showReport && reportRows.length > 0 && (
              <div className="d2t-table-wrapper">
                <table className="d2t-table">
                  <thead><tr><th>Employee</th><th>Area</th><th>Task</th><th>Title</th><th>Version</th><th>Level</th><th>Declarations</th><th>Signed date</th><th>Status</th><th>Approved at</th><th>Approved by</th></tr></thead>
                  <tbody>
                    {reportRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.employee_name} (ID: {row.employee_id})</td>
                        <td>{row.area}</td>
                        <td>{row.task_code}</td><td>{row.title || ''}</td><td>{row.doc_version || ''}</td>
                        <td>{row.level}</td><td>{row.all_checkboxes_confirmed ? 'All confirmed' : 'Not all confirmed'}</td>
                        <td>{formatDate(row.signed_date)}</td><td>{row.status}</td><td>{row.approved_at ? formatDateTime(row.approved_at) : ''}</td><td>{row.approved_by || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {message && (
          <p className={`d2t-message ${msgType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>{message}</p>
        )}

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// Admin: Pending Approvals Dashboard
// ------------------------------------------------------
function PendingApprovalsPage() {
  const navigate = useNavigate();
  const { user, setUser, checked } = useMe();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('info');
  const [rejectReasons, setRejectReasons] = useState({});
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/pending-approvals');
      setRows(res.data || []);
    } catch {
      setMessage('Error loading pending approvals.'); setMsgType('error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!checked) return;
    if (!user) { navigate('/login?redirect=/admin/pending', { replace: true }); return; }
    load();
  }, [checked, user, navigate, load]);

  const handleApprove = async (id, row) => {
    setActing(id); setMessage('');
    try {
      await axios.post(`/api/signoffs/${id}/approve`);
      setMessage(`Approved: ${row.employee_name} – ${row.task_code}`); setMsgType('success');
      await load();
    } catch (err) { setMessage(err.response?.data?.error || 'Error approving.'); setMsgType('error'); }
    finally { setActing(null); }
  };

  const handleReject = async (id, row) => {
    const reason = rejectReasons[id] || '';
    if (!reason.trim()) { setMessage('Please enter a rejection reason first.'); setMsgType('error'); return; }
    setActing(id); setMessage('');
    try {
      await axios.post(`/api/signoffs/${id}/reject`, { reason });
      setMessage(`Rejected: ${row.employee_name} – ${row.task_code}`); setMsgType('success');
      await load();
    } catch (err) { setMessage(err.response?.data?.error || 'Error rejecting.'); setMsgType('error'); }
    finally { setActing(null); }
  };

  if (!checked) return <div className="d2t-page"><div className="d2t-card"><LogoHeader /><p>Checking login…</p></div></div>;
  if (!user) return null;

  return (
    <div className="d2t-page"><div className="d2t-card">
      <LogoHeader />
      <div className="d2t-header-bar">
        <div>
          <h1 className="d2t-title">Pending Approvals</h1>
          <p className="d2t-subtitle">All sign-offs awaiting authorisation across all areas.</p>
        </div>
        <div className="d2t-help">
          <Link to="/admin" className="d2t-button d2t-button--small" style={{ marginRight: '0.5rem' }}>← Admin</Link>
          <LogoutButton onLoggedOut={() => setUser(null)} />
        </div>
      </div>
      {message && <p className={`d2t-message ${msgType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>{message}</p>}
      {loading && <p>Loading…</p>}
      {!loading && rows.length === 0 && (
        <p style={{ color: '#16a34a', fontWeight: 600 }}>✓ No pending approvals — all sign-offs are up to date.</p>
      )}
      {!loading && rows.map(row => (
        <div key={row.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', background: '#fffbeb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div><strong>{row.employee_name}</strong> <span style={{ color: '#6b7280' }}>(ID: {row.employee_id})</span></div>
            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Submitted: {formatDate(row.created_at)}</span>
          </div>
          <div style={{ fontSize: '0.88rem', marginBottom: '0.75rem', color: '#374151' }}>
            <strong>Area:</strong> {row.area} &nbsp;|&nbsp; <strong>Task:</strong> {row.task_code} &nbsp;|&nbsp; <strong>Level:</strong> {row.level}<br />
            {row.title && <span><strong>Guide:</strong> {row.title}<br /></span>}
            <strong>Version:</strong> {row.doc_version || '—'} &nbsp;|&nbsp; <strong>Signed date:</strong> {formatDate(row.signed_date)}
          </div>
          <div className="d2t-field" style={{ marginBottom: '0.5rem' }}>
            <label className="d2t-label" style={{ fontSize: '0.8rem' }}>Rejection reason (required to reject)</label>
            <textarea className="d2t-textarea" rows={2} value={rejectReasons[row.id] || ''}
              onChange={e => setRejectReasons(prev => ({ ...prev, [row.id]: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="d2t-button" onClick={() => handleApprove(row.id, row)} disabled={acting === row.id}>
              {acting === row.id ? 'Working…' : 'Approve'}
            </button>
            <button type="button" className="d2t-button d2t-button--danger" onClick={() => handleReject(row.id, row)} disabled={acting === row.id}>
              {acting === row.id ? 'Working…' : 'Reject'}
            </button>
          </div>
        </div>
      ))}
      <FooterImage />
    </div></div>
  );
}

// ------------------------------------------------------
// Admin: Operator Accounts
// ------------------------------------------------------
function OperatorAccountsPage() {
  const navigate = useNavigate();
  const { user, setUser, checked } = useMe();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('info');
  const [resettingId, setResettingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get('/api/admin/operator-accounts'); setAccounts(res.data || []); }
    catch { setMessage('Error loading operator accounts.'); setMsgType('error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!checked) return;
    if (!user) { navigate('/login?redirect=/admin/operators', { replace: true }); return; }
    if (user.role !== 'admin') { setMessage('Admin access required.'); setMsgType('error'); setLoading(false); return; }
    load();
  }, [checked, user, navigate, load]);

  const handleReset = async (acc) => {
    if (!window.confirm(`Reset password for ${acc.full_name}? They will need to verify via date of birth next time they log in.`)) return;
    setResettingId(acc.id); setMessage('');
    try {
      await axios.post(`/api/admin/operator-accounts/${acc.id}/reset`);
      setMessage(`Password reset for ${acc.full_name}. They will be prompted to set a new password on next login.`); setMsgType('success');
      await load();
    } catch (err) { setMessage(err.response?.data?.error || 'Error resetting account.'); setMsgType('error'); }
    finally { setResettingId(null); }
  };

  if (!checked) return <div className="d2t-page"><div className="d2t-card"><LogoHeader /><p>Checking login…</p></div></div>;
  if (!user) return null;

  return (
    <div className="d2t-page"><div className="d2t-card">
      <LogoHeader />
      <div className="d2t-header-bar">
        <div><h1 className="d2t-title">Operator Accounts</h1><p className="d2t-subtitle">View all operator accounts and reset passwords.</p></div>
        <div className="d2t-help">
          <Link to="/admin" className="d2t-button d2t-button--small" style={{ marginRight: '0.5rem' }}>← Admin</Link>
          <LogoutButton onLoggedOut={() => setUser(null)} />
        </div>
      </div>
      {message && <p className={`d2t-message ${msgType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>{message}</p>}
      {loading && <p>Loading…</p>}
      {!loading && accounts.length === 0 && <p>No operator accounts found. Accounts are created automatically when an operator first logs in via date of birth.</p>}
      {!loading && accounts.length > 0 && (
        <div className="d2t-table-wrapper">
          <table className="d2t-table">
            <thead><tr><th>Name</th><th>Employee ID</th><th>Has password</th><th>Must change</th><th>Last login</th><th>Created</th><th>Action</th></tr></thead>
            <tbody>
              {accounts.map(acc => (
                <tr key={acc.id}>
                  <td>{acc.full_name}</td>
                  <td>{acc.employee_id}</td>
                  <td>{acc.has_password ? '✓' : <span style={{ color: '#dc2626' }}>No password set</span>}</td>
                  <td>{acc.must_change_password ? <span style={{ color: '#d97706' }}>Yes</span> : 'No'}</td>
                  <td>{acc.last_login_at ? formatDateTime(acc.last_login_at) : '—'}</td>
                  <td>{formatDate(acc.created_at)}</td>
                  <td>
                    <button type="button" className="d2t-button d2t-button--small d2t-button--danger"
                      onClick={() => handleReset(acc)} disabled={resettingId === acc.id}>
                      {resettingId === acc.id ? 'Resetting…' : 'Reset password'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FooterImage />
    </div></div>
  );
}

// ------------------------------------------------------
// Admin: Audit Log
// ------------------------------------------------------
function AuditLogPage() {
  const navigate = useNavigate();
  const { user, setUser, checked } = useMe();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!checked) return;
    if (!user) { navigate('/login?redirect=/admin/audit', { replace: true }); return; }
    if (user.role !== 'admin') { setMessage('Admin access required.'); setLoading(false); return; }
    axios.get('/api/admin/audit-log?limit=200')
      .then(res => setRows(res.data || []))
      .catch(() => setMessage('Error loading audit log.'))
      .finally(() => setLoading(false));
  }, [checked, user, navigate]);

  const eventLabel = e => ({
    admin_login: 'Admin login', operator_login: 'Operator login', operator_logout: 'Operator logout',
    operator_dob_verify: 'DOB verification', operator_password_change: 'Password changed',
    operator_account_reset: 'Account reset by admin', signoff_submitted: 'Sign-off submitted',
    signoff_approved: 'Sign-off approved', signoff_rejected: 'Sign-off rejected'
  }[e] || e);

  const eventColor = e => {
    if (e.includes('reject') || e.includes('reset')) return '#dc2626';
    if (e.includes('approved') || e.includes('login') || e.includes('verify')) return '#16a34a';
    return '#374151';
  };

  const filtered = rows.filter(r => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return r.event_type.includes(q) || (r.operator_name || '').toLowerCase().includes(q) ||
           (r.user_email || '').toLowerCase().includes(q) || (r.detail || '').toLowerCase().includes(q);
  });

  if (!checked) return <div className="d2t-page"><div className="d2t-card"><LogoHeader /><p>Checking login…</p></div></div>;
  if (!user) return null;

  return (
    <div className="d2t-page"><div className="d2t-card">
      <LogoHeader />
      <div className="d2t-header-bar">
        <div><h1 className="d2t-title">Audit Log</h1><p className="d2t-subtitle">Last 200 events, most recent first.</p></div>
        <div className="d2t-help">
          <Link to="/admin" className="d2t-button d2t-button--small" style={{ marginRight: '0.5rem' }}>← Admin</Link>
          <LogoutButton onLoggedOut={() => setUser(null)} />
        </div>
      </div>
      <div className="d2t-field">
        <input className="d2t-input" placeholder="Filter by name, event, email…" value={filter} onChange={e => setFilter(e.target.value)} />
      </div>
      {message && <p className="d2t-message d2t-message--error">{message}</p>}
      {loading && <p>Loading…</p>}
      {!loading && filtered.length === 0 && <p>No audit log entries found.</p>}
      {!loading && filtered.length > 0 && (
        <div className="d2t-table-wrapper">
          <table className="d2t-table">
            <thead><tr><th>When</th><th>Event</th><th>Operator</th><th>User</th><th>Detail</th></tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{formatDateTime(r.created_at)}</td>
                  <td><span style={{ color: eventColor(r.event_type), fontWeight: 600, fontSize: '0.82rem' }}>{eventLabel(r.event_type)}</span></td>
                  <td>{r.operator_name || '—'}</td>
                  <td>{r.user_email || '—'}</td>
                  <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>{r.detail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FooterImage />
    </div></div>
  );
}

// ------------------------------------------------------
// Manage Users page
// ------------------------------------------------------
function ManageUsersPage() {
  const navigate = useNavigate();
  const { user, setUser, checked } = useMe();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('info');

  const [editingId, setEditingId] = useState(null);
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState('admin');
  const [formPassword, setFormPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/users');
      setUsers(res.data || []);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error loading users.');
      setMsgType('error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!checked) return;
    if (!user) { navigate('/login?redirect=/admin/users', { replace: true }); return; }
    if (user.role !== 'admin') { setMessage('You do not have admin access.'); setMsgType('error'); setLoading(false); return; }
    loadUsers();
  }, [checked, user, navigate]);

  const resetForm = () => { setEditingId(null); setFormEmail(''); setFormRole('admin'); setFormPassword(''); setMessage(''); };
  const startEdit = (u) => { setEditingId(u.id); setFormEmail(u.email); setFormRole(u.role); setFormPassword(''); setMessage(''); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(''); setMsgType('info');
    if (!formEmail) { setMessage('Email is required.'); setMsgType('error'); return; }
    if (!editingId && !formPassword) { setMessage('Password is required when creating a new user.'); setMsgType('error'); return; }
    if (formPassword && formPassword.length < 8) { setMessage('Password must be at least 8 characters.'); setMsgType('error'); return; }
    setSaving(true);
    try {
      if (editingId) {
        const body = { email: formEmail, role: formRole };
        if (formPassword) body.password = formPassword;
        await axios.put(`/api/admin/users/${editingId}`, body);
        setMessage('User updated successfully.');
      } else {
        await axios.post('/api/admin/users', { email: formEmail, role: formRole, password: formPassword });
        setMessage('User created successfully.');
      }
      setMsgType('success');
      resetForm();
      await loadUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error saving user.'); setMsgType('error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (u.id === user?.id) { setMessage('You cannot delete your own account.'); setMsgType('error'); return; }
    if (!window.confirm(`Delete user "${u.email}"? This cannot be undone.`)) return;
    setMessage('');
    try {
      await axios.delete(`/api/admin/users/${u.id}`);
      setMessage(`User "${u.email}" deleted.`); setMsgType('success');
      if (editingId === u.id) resetForm();
      await loadUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error deleting user.'); setMsgType('error');
    }
  };

  if (!checked) return <div className="d2t-page"><div className="d2t-card"><LogoHeader /><p>Checking login...</p></div></div>;
  if (!user) return null;

  return (
    <div className="d2t-page">
      <div className="d2t-card">
        <LogoHeader />
        <div className="d2t-header-bar">
          <div>
            <h1 className="d2t-title">Manage Users</h1>
            <p className="d2t-subtitle">Create, edit or delete admin/HR users.</p>
          </div>
          <div className="d2t-help">
            {user.email}{' '}
            <Link to="/admin" className="d2t-button d2t-button--small" style={{ marginRight: '0.5rem' }}>← Back to admin</Link>
            <LogoutButton onLoggedOut={() => setUser(null)} />
          </div>
        </div>

        <h2 className="d2t-title" style={{ fontSize: '1.1rem', marginTop: '1rem' }}>{editingId ? 'Edit user' : 'Create new user'}</h2>

        <form onSubmit={handleSubmit}>
          <div className="d2t-field"><label className="d2t-label">Email</label><input type="email" className="d2t-input" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} required /></div>
          <div className="d2t-field">
            <label className="d2t-label">Role</label>
            <select className="d2t-select" value={formRole} onChange={(e) => setFormRole(e.target.value)}>
              <option value="admin">admin</option>
              <option value="hr">hr</option>
            </select>
          </div>
          <div className="d2t-field">
            <label className="d2t-label">{editingId ? 'New password (leave blank to keep current)' : 'Password'}</label>
            <input type="password" className="d2t-input" value={formPassword} onChange={(e) => setFormPassword(e.target.value)} placeholder={editingId ? 'Leave blank to keep unchanged' : ''} minLength={formPassword ? 8 : undefined} />
            <p className="d2t-help">Minimum 8 characters.</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" className="d2t-button d2t-button--full" disabled={saving}>{saving ? 'Saving…' : editingId ? 'Update user' : 'Create user'}</button>
            {editingId && <button type="button" className="d2t-button" onClick={resetForm} style={{ whiteSpace: 'nowrap' }}>Cancel edit</button>}
          </div>
        </form>

        {message && <p className={`d2t-message ${msgType === 'success' ? 'd2t-message--success' : 'd2t-message--error'}`}>{message}</p>}

        <h2 className="d2t-title" style={{ fontSize: '1.1rem', marginTop: '1.5rem' }}>Existing users</h2>
        {loading ? <p>Loading users…</p> : users.length === 0 ? <p>No users found.</p> : (
          <div className="d2t-table-wrapper">
            <table className="d2t-table">
              <thead><tr><th>Email</th><th>Role</th><th>Edit</th><th>Delete</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={editingId === u.id ? { background: 'rgba(0,0,0,0.04)' } : {}}>
                    <td>{u.email}{u.id === user.id ? ' (you)' : ''}</td>
                    <td>{u.role}</td>
                    <td><button type="button" className="d2t-button d2t-button--small" onClick={() => startEdit(u)}>Edit</button></td>
                    <td><button type="button" className="d2t-button d2t-button--small d2t-button--danger" onClick={() => handleDelete(u)} disabled={u.id === user.id} title={u.id === user.id ? 'Cannot delete your own account' : ''}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <FooterImage />
      </div>
    </div>
  );
}

// ------------------------------------------------------
// Root App with routes
// ------------------------------------------------------
function App() {
  return (
    <Routes>
      {/* Operator routes */}
      <Route path="/" element={<SignoffForm />} />
      <Route path="/my-signoffs" element={<MySignoffsPage />} />
      <Route path="/operator-login" element={<OperatorLoginPage />} />
      <Route path="/operator-login-dob" element={<OperatorLoginDobPage />} />
      <Route path="/operator-change-password" element={<OperatorChangePasswordPage />} />

      {/* HR/Admin routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/authorise" element={<AuthorisePage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin/pending" element={<PendingApprovalsPage />} />
      <Route path="/admin/operators" element={<OperatorAccountsPage />} />
      <Route path="/admin/audit" element={<AuditLogPage />} />
      <Route path="/admin/users" element={<ManageUsersPage />} />

      <Route path="*" element={<Navigate to="/operator-login" replace />} />
    </Routes>
  );
}

export default App;